import type { Placement } from '@popperjs/core';
import type { OffsetsFunction } from '@popperjs/core/lib/modifiers/offset';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';

import IconCaretDown from '@/assets/Icons/IconCaretDown';
import { useMessages } from '@/hooks/messages';

import { dropdownMessageDefinitions } from './messages';

export interface CustomDropdownOption {
  label: ReactNode;
  value: string;
}

export interface CustomDropdownProps {
  value: string | null;
  options: CustomDropdownOption[];
  onChange: (value: string) => void;
  onBlur: () => void;
  placement?: Placement;
  offset?: OffsetsFunction | [number | null | undefined, number | null | undefined];
  disabled?: boolean;
  fullWidth?: boolean;
  emptyPlaceholder?: string;
  isArrowIcon?: boolean;
}

export const CustomDropdown: FC<CustomDropdownProps> = ({
  value,
  options,
  onChange,
  onBlur,
  placement,
  disabled,
  offset,
  fullWidth,
  emptyPlaceholder,
  isArrowIcon,
}) => {
  const messages = useMessages(dropdownMessageDefinitions);
  const [open, setOpen] = useState<boolean>(false);
  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);
  const [popperRef, setPopperRef] = useState<HTMLDivElement | null>(null);

  const { styles, attributes } = usePopper(buttonRef, popperRef, {
    placement: placement || 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: offset || [0, 0],
        },
      },
    ],
  });

  const toggleOpen = () => {
    if (!disabled) {
      if (open) {
        onBlur();
      }
      setOpen((x) => !x);
    }
  };

  const handleDocumentClick = useCallback(
    (event: MouseEvent) => {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      if (buttonRef?.contains(event.target) || popperRef?.contains(event.target)) {
        return;
      }

      setOpen(false);
      onBlur();
    },
    [buttonRef, popperRef, onBlur],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [handleDocumentClick]);

  return (
    <>
      <button
        type='button'
        ref={setButtonRef}
        data-testid='custom-dropdown-button'
        className={`form-input border-c1 focus:!border-focusInput focus:shadow-focus-input dark:border-c1 inline-flex items-center !rounded-md text-left text-sm font-medium outline-2 ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${fullWidth ? 'w-full' : ''}`}
        onClick={toggleOpen}
      >
        <span className='block min-w-0 flex-1 truncate text-left'>
          {value === null
            ? emptyPlaceholder === undefined
              ? messages.emptyPlaceholder()
              : emptyPlaceholder
            : options.find((x) => x.value === value)?.label}
        </span>
        {isArrowIcon && (
          <span className='block shrink-0'>
            <IconCaretDown className='ml-2 block ltr:ml-1 rtl:mr-1' />
          </span>
        )}
      </button>
      {open &&
        /* Portal is needed when used inside of element with overflow:hidden e.g. mantine-datatable. */
        ReactDOM.createPortal(
          <div
            ref={setPopperRef}
            style={styles.popper}
            {...attributes.popper}
            className='dropdown z-[10010]'
            data-testid='custom-dropdown-options'
          >
            <ul className='!min-w-[170px]'>
              {options.map((option) => (
                <li key={option.value}>
                  <button
                    type='button'
                    data-testid={`dropdown-option-${option.value}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setOpen(false);
                      onChange(option.value);
                      onBlur();
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </>
  );
};
